import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Search, Briefcase, Download, Loader2, Lock,
  AlertCircle, WifiOff, Sparkles,
  FileText, RefreshCw, TrendingUp,
  Target, Clock, ChevronRight, ChevronLeft, Copy, Check,
  ExternalLink, Star, Mail, Phone, MapPin,
  Zap, X, Filter
} from 'lucide-react';
import api from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { toast } from 'sonner';
import { useJobContext } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';
import { 
  LinkedInIcon, FacebookIcon, InstagramIcon, 
  XIcon, SocialLink, getSocialsFromLead 
} from '../components/SocialIcons';

const POPULAR_SEARCHES = [
  { icon: '🏨', label: 'Hotels in Miami', query: 'hotels in Miami' },
  { icon: '🏠', label: 'Real Estate NYC', query: 'real estate agents NYC' },
  { icon: '🦷', label: 'Dentists Chicago', query: 'dentists in Chicago' },
  { icon: '💻', label: 'Web Design SF', query: 'web design agencies San Francisco' },
  { icon: '🍕', label: 'Restaurants Austin', query: 'restaurants in Austin' },
  { icon: '⚖️', label: 'Law Firms Houston', query: 'law firms in Houston' },
];

let completedLeadsCache = [];
let cacheQueryKey = null;  // Tracks which search the cache belongs to, prevents stale cross-search data

// ─── Smooth progress hook ───
function useSmoothProgress(targetPercent) {
  // Start at 5 to match backend's initial progress_percent, prevents
  // visual jump from 0→5 when the first real update arrives
  const [displayPercent, setDisplayPercent] = useState(5);
  const animFrameRef = useRef(null);
  
  useEffect(() => {
    const target = Math.max(0, Math.min(100, targetPercent || 0));
    
    const animate = () => {
      setDisplayPercent(prev => {
        if (Math.abs(prev - target) < 0.5) return target;
        // Ease towards target,  faster when far, slower when close
        const diff = target - prev;
        const step = diff * 0.12 + (diff > 0 ? 0.5 : -0.5);
        const next = prev + step;
        // Don't overshoot
        if ((step > 0 && next >= target) || (step < 0 && next <= target)) return target;
        return next;
      });
      animFrameRef.current = requestAnimationFrame(animate);
    };
    
    animFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [targetPercent]);
  
  return displayPercent;
}

export default function SearchPage({ 
  isSearching, searchProgress, pollingQuery,
  hasFailed, errorMessage,
  latestLeads, clearLatestLeads,
  activeJobs = [], queuedJobs = [],
  completedJob = null, failedJob = null,
  isConnected = false,
}) {
  const { state: jobState, dispatch, dismissCompleted, dismissFailed, refreshJobs } = useJobContext();
  const { user } = useAuth();
  const activeJobsArr = Object.values(jobState.activeJobs);
  const queuedJobsArr = Object.values(jobState.queuedJobs);
  const effectiveActiveJobs = activeJobsArr.length > 0 ? activeJobsArr : activeJobs;
  const effectiveQueuedJobs = queuedJobsArr.length > 0 ? queuedJobsArr : queuedJobs;
  const effectiveCompletedJob = jobState.completedJobs[0] || completedJob;
  const effectiveFailedJob = jobState.failedJob || failedJob;

  const navigate = useNavigate();
  
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [showQueryHints, setShowQueryHints] = useState(false);
  const [serviceContext, setServiceContext] = useState('');
  const [originalServiceContext, setOriginalServiceContext] = useState('');
  const [leads, setLeads] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loadingLastResults, setLoadingLastResults] = useState(true);
  const [showSuccessPulse, setShowSuccessPulse] = useState(false);
  const [completionBanner, setCompletionBanner] = useState(null); // { query, count }
  const [upsellDismissed, setUpsellDismissed] = useState(false);
  const [plan, setPlan] = useState('free');
  const [planLoading, setPlanLoading] = useState(true);
  const [credits, setCredits] = useState({ used: 0, limit: 0, loading: true });
  const [planLimit, setPlanLimit] = useState(50);
  const [showServiceEdit, setShowServiceEdit] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [copiedPitch, setCopiedPitch] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [hotThreshold, setHotThreshold] = useState(70);
  const [completedQuery, setCompletedQuery] = useState('');
  
  const queryInputRef = useRef(null);
  const skipFetchRef = useRef(false);
  const resultsRef = useRef(null);
  const shownCompletedRef = useRef(new Set()); // track job IDs already shown
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  useEffect(() => {
    loadUserData();
    fetchCredits();
    if (refreshJobs) refreshJobs();
    try {
      api.get('/api/auth/me').then(res => {
        if (res.data?.hot_threshold) setHotThreshold(res.data.hot_threshold);
      }).catch(() => {});
    } catch {}
  }, []);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  // Score locking removed,  free users get full experience

  const fetchCredits = async () => {
    try {
      const res = await api.get('/api/sidebar/credits');
      setCredits({ used: res.data.searches_used || 0, limit: res.data.searches_limit || 0, loading: false });
    } catch { setCredits(c => ({ ...c, loading: false })); }
  };

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  useEffect(() => {
    loadUserData();
    fetchCredits();
    try {
      api.get('/api/auth/me').then(res => {
        if (res.data?.hot_threshold) setHotThreshold(res.data.hot_threshold);
      }).catch(() => {});
    } catch {}
  }, []);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  // Score locking removed,  free users get full experience

  const loadUserData = async () => {
      try {
        const res = await api.get('/api/auth/me');
        const userPlan = res.data.plan || 'free';
        setPlan(userPlan);
        // Sync leads_per_search from plan
        if (res.data.leads_per_search) {
          setPlanLimit(res.data.leads_per_search);
        }
        const svc = res.data.service_name || 'B2B software';
        setServiceContext(svc);
        setOriginalServiceContext(svc);
      } catch {
        setPlan('free');
        setServiceContext('B2B software');
        setOriginalServiceContext('B2B software');
      } finally { setPlanLoading(false); }
    };

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  useEffect(() => {
    // Validate cached data before using it — if leads have empty values for
    // key fields, the cache is corrupt (likely from an empty WebSocket push).
    const cacheIsValid = completedLeadsCache.length > 0 && cacheQueryKey &&
      completedLeadsCache.some(l => l.company_name && l.company_name !== 'company_name');
    
    if (cacheIsValid) {
      setLeads(completedLeadsCache);
      setLoadingLastResults(false);
      return;
    }
    
    // Cache invalid or empty — clear it and fetch fresh data like HistoryPage
    if (completedLeadsCache.length > 0 && !cacheIsValid) {
      completedLeadsCache = [];
      cacheQueryKey = null;
      skipFetchRef.current = false;
    }
    const fetchLastResults = async () => {
      // Don't fetch if a completed job already provided leads with valid cache key
      if (skipFetchRef.current || (leads.length > 0 && cacheQueryKey) || (completedLeadsCache.length > 0 && cacheQueryKey)) {
        setLeads(completedLeadsCache.length > 0 ? completedLeadsCache : leads);
        setLoadingLastResults(false);
        return;
      }
      setLoadingLastResults(true);
      try {
        const res = await api.get('/api/leads/history', { params: { limit: planLimit || 50 } });
        const allLeads = res.data.leads || [];
        if (allLeads.length > 0) {
          const grouped = {};
          allLeads.forEach(lead => {
            const key = lead.search_query || 'Unknown Search';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(lead);
          });
          const latestQuery = allLeads[0].search_query || Object.keys(grouped)[0];
          const latestLeads = grouped[latestQuery] || allLeads;
          setLeads(latestLeads);
          setCompletedQuery(latestQuery);
          completedLeadsCache = latestLeads;
          cacheQueryKey = latestQuery;
        }
      } catch {
        const cached = localStorage.getItem('last_search_results');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const cachedLeads = parsed.leads || [];
            // Only use cached leads if they contain real data
            if (cachedLeads.length > 0 && cachedLeads.some(l => l.company_name && l.company_name !== 'company_name')) {
              setLeads(cachedLeads);
              completedLeadsCache = cachedLeads;
              if (parsed.query) cacheQueryKey = parsed.query;
            }
          } catch {}
        }
      } finally { setLoadingLastResults(false); }
    };
    fetchLastResults();
    
    // On mount (especially after onboarding), fetch search status immediately
    // to catch active or recently completed jobs before WebSocket connects.
    const checkRecentActivity = async () => {
      try {
        const res = await api.get('/api/search/status', { params: { _t: Date.now() } });
        const data = res.data;
        
        // Handle completed job (banner + leads)
        if (data.completed_job && data.completed_job.leads?.length > 0) {
          const job = data.completed_job;
          if (!shownCompletedRef.current.has(job.job_id)) {
            shownCompletedRef.current.add(job.job_id);
            setLeads(job.leads);
            setCompletedQuery(job.query || '');
            completedLeadsCache = job.leads;
            cacheQueryKey = job.query || '';
            skipFetchRef.current = true;
            setShowSuccessPulse(true);
            setCompletionBanner({ query: job.query, count: job.leads.length });
            setLoadingLastResults(false);
            setTimeout(() => setShowSuccessPulse(false), 600);
            setTimeout(() => setCompletionBanner(null), 8000);
          }
        }
        
        // Handle active jobs (search card) — WebSocket will take over after connect
        if (data.is_searching || data.active_jobs?.length > 0) {
          setLoadingLastResults(false);
        }
      } catch {}
    };
    
    checkRecentActivity();
  }, [refetchTrigger]);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  // Listen for individual lead updates (enrich/score completion)
  const { leadUpdateEvent } = useJobContext() || {};
  useEffect(() => {
    if (leadUpdateEvent?.company_name && leadUpdateEvent?.lead) {
      setLeads(prev => {
        const updated = prev.map(lead => {
          if (lead.company_name === leadUpdateEvent.company_name) {
            return { ...lead, ...leadUpdateEvent.lead };
          }
          return lead;
        });
        // Update cache with new data, preserve key
        completedLeadsCache = updated;
        localStorage.setItem('last_search_results', JSON.stringify({
          leads: updated, query: completedQuery, timestamp: Date.now(),
        }));
        return updated;
      });
    }
  }, [leadUpdateEvent]);

  useEffect(() => {
    if (effectiveCompletedJob && effectiveCompletedJob.job_id) {
      // Only show banner/scroll for newly completed jobs, not on every mount
      if (shownCompletedRef.current.has(effectiveCompletedJob.job_id)) {
        // Already shown — just update leads silently if needed
        const cachedLeads = effectiveCompletedJob.leads || [];
        if (cachedLeads.length > 0 && leads.length === 0) {
          setLeads(cachedLeads);
          completedLeadsCache = cachedLeads;
          cacheQueryKey = effectiveCompletedJob.query || '';
        }
        return;
      }
      shownCompletedRef.current.add(effectiveCompletedJob.job_id);
      
      const newLeads = effectiveCompletedJob.leads || [];
      if (newLeads.length > 0) {
        fetchCredits();
        setLeads(newLeads);
        setCompletedQuery(effectiveCompletedJob.query || '');
        completedLeadsCache = newLeads;
        cacheQueryKey = effectiveCompletedJob.query || '';
        skipFetchRef.current = true;
        setShowSuccessPulse(true);
        setCompletionBanner({ query: effectiveCompletedJob.query, count: newLeads.length });
        toast.success(`Search complete,  ${newLeads.length} lead${newLeads.length !== 1 ? 's' : ''} found for "${effectiveCompletedJob.query}"`, { id: 'search-done', duration: 6000 });
        setTimeout(() => setShowSuccessPulse(false), 600);
        setTimeout(() => setCompletionBanner(null), 8000);
        
        // Also fetch from database to ensure we have complete data
        // (WebSocket payload may be truncated or missing fields)
        setTimeout(async () => {
          try {
            const res = await api.get('/api/leads/history', { params: { limit: planLimit || 50 } });
            const allLeads = res.data.leads || [];
            if (allLeads.length > 0) {
              const grouped = {};
              allLeads.forEach(lead => {
                const key = lead.search_query || 'Unknown Search';
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(lead);
              });
              const latestQuery = effectiveCompletedJob.query || allLeads[0].search_query || Object.keys(grouped)[0];
              const dbLeads = grouped[latestQuery] || allLeads;
              if (dbLeads.length > 0) {
                setLeads(dbLeads);
                completedLeadsCache = dbLeads;
                cacheQueryKey = latestQuery;
              }
            }
          } catch {}
        }, 1000);
        localStorage.setItem('last_search_results', JSON.stringify({
          leads: newLeads, query: effectiveCompletedJob.query, timestamp: Date.now(),
        }));
        setLoadingLastResults(false);
        if (dismissCompleted) dismissCompleted();
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      } else {
        // Leads arrived empty,  worker may still be saving to DB.
        // Always force a fresh fetch after a short delay to pick up the leads.
        // We do not rely on result_count because the API may not provide it.
        skipFetchRef.current = false;
        completedLeadsCache = [];
        cacheQueryKey = null;
        setTimeout(() => {
          setLeads([]);
          setLoadingLastResults(true);
          setRefetchTrigger(prev => prev + 1);
        }, 500);
        // Dismiss the completed job after scheduling refetch to avoid it
        // staying in state and causing repeated effects.
        if (dismissCompleted) dismissCompleted();
      }
    }
  }, [effectiveCompletedJob]);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  useEffect(() => {
    if (effectiveFailedJob) {
      toast.error(`Search "${effectiveFailedJob.query}" failed: ${effectiveFailedJob.error}`, {
        id: `search-failed-${effectiveFailedJob.job_id}`, duration: 8000,
      });
      if (dismissFailed) dismissFailed();
    }
  }, [effectiveFailedJob]);

  // Close query hints tooltip when clicking outside
  useEffect(() => {
    if (!showQueryHints) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.query-hints-container')) {
        setShowQueryHints(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showQueryHints]);

  useEffect(() => {
    const hOnline = () => setIsOnline(true);
    const hOffline = () => setIsOnline(false);
    window.addEventListener('online', hOnline);
    window.addEventListener('offline', hOffline);
    return () => { window.removeEventListener('online', hOnline); window.removeEventListener('offline', hOffline); };
  }, []);

  // Debounced location autocomplete
  const locationFetchRef = useRef(null);
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocation(value);
    
    if (locationFetchRef.current) clearTimeout(locationFetchRef.current);
    
    if (value.trim().length < 2) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }
    
    setLocationLoading(true);
    locationFetchRef.current = setTimeout(async () => {
      try {
        // Use free Nominatim API for geocoding suggestions
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept': 'application/json' } }
        );
        const data = await res.json();
        const suggestions = data.map(item => ({
          name: item.display_name.split(',')[0],
          city: item.address?.city || item.address?.town || item.address?.village || '',
          state: item.address?.state || item.address?.county || '',
          country: item.address?.country || '',
          full: item.display_name,
        })).filter((s, i, arr) => arr.findIndex(a => {
          const key = (s.city || s.state || s.country) + '|' + s.state + '|' + s.country;
          const prevKey = (a.city || a.state || a.country) + '|' + a.state + '|' + a.country;
          return key === prevKey;
        }) === i);
        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(suggestions.length > 0);
      } catch {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      } finally {
        setLocationLoading(false);
      }
    }, 500);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    // Merge business type and location into final query
    const mergedQuery = location.trim()
      ? `${businessType.trim()} in ${location.trim()}`
      : businessType.trim();
    setQuery(mergedQuery);
    e?.preventDefault();
    if (!businessType.trim() || !location.trim()) {
      toast.warning('Please enter both Target Business Type and Location to search.');
      return;
    }
    if (!isOnline) { toast.error('Cannot search while offline.'); return; }
    setError('');
    setSearchLoading(true);
    
    // Clear stale cache when starting a new search
    completedLeadsCache = [];
    cacheQueryKey = null;
    localStorage.removeItem('last_search_results');
    try {
      const payload = {
        query: mergedQuery.trim(), limit: Math.min(planLimit, 50), enrich: true, score: true,
        service_context: serviceContext || 'B2B software',
      };
      if (user?.organization_id && campaignName.trim()) {
        payload.campaign_name = campaignName.trim();
      }
      const response = await api.post('/api/search', payload);
      // Dispatch to JobContext for immediate active/queue card
      if (response.data.status === 'started') {
        toast.success('Search started!', { id: 'search-started', duration: 4000 });
        dispatch({ type: 'JOB_STARTED', job_id: response.data.job_id, query: mergedQuery.trim(), progress: 'Starting search...', progress_percent: 5, started_at: new Date().toISOString() });
        fetchCredits();
      } else if (response.data.status === 'queued') {
        const reason = response.data.queue_reason;
        const pos = response.data.queue_position || '?';
        const msg = reason === 'concurrency_limit'
          ? `Search queued,  you've reached your plan's limit (${response.data.plan_active || 0}/${response.data.plan_concurrency || 1} running). #${pos} in line.`
          : `Search queued,  all workers busy. You are #${pos} in line.`;
        toast.info(msg, { id: 'search-queued', duration: 6000 });
        dispatch({ type: 'JOB_QUEUED', job_id: response.data.job_id, query: mergedQuery.trim(), queued_at: new Date().toISOString() });
        fetchCredits();
      }
    } catch (err) {
      if (err.response?.status === 409) toast.info('This search is already running or queued.');
      else if (err.response?.status === 429) toast.info(err.response?.data?.detail || 'Limit reached.', { duration: 8000 });
      else { const msg = err.response?.data?.detail || 'Failed to start search.'; setError(msg); toast.error(msg); }
    } finally { setSearchLoading(false); }
  };

  const handleServiceContextBlur = async () => {
    if (serviceContext === originalServiceContext) return;
    try {
      await api.put('/api/auth/profile', { service_name: serviceContext });
      setOriginalServiceContext(serviceContext);
    } catch (err) {
      if (err.response?.status === 429 || err.response?.status === 403) {
        setShowUpgradeModal(true);
        setServiceContext(originalServiceContext);
      }
    }
  };

  const handleExportCSV = async () => {
    if (leads.length === 0) { toast.info('No leads to export'); return; }
    setExporting(true);
    try {
      const response = await api.post('/api/leads/export', { leads }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ileadx_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`CSV downloaded! ${leads.length} leads exported.`);
    } catch { toast.error('Export failed.'); }
    finally { setExporting(false); }
  };

  const copyPitch = (lead, index) => {
    let pitchText = lead.pitch_angle || '';
    try {
      if (typeof pitchText === 'string' && pitchText.startsWith('[')) {
        const angles = JSON.parse(pitchText);
        pitchText = (angles[0] || '').replace(/^\[?(Empathy|ROI|Urgency|Data-Driven|Direct|Data)(\s*angle)?[\s:]*[, –-]?\s*/i, '').replace(/\]$/, '').trim();
      }
    } catch {}
    navigator.clipboard.writeText(pitchText);
    setCopiedPitch(index);
    toast.success('Pitch copied!');
    setTimeout(() => setCopiedPitch(null), 2000);
  };

  const getFilteredLeads = () => {
    if (activeFilter === 'all') return leads;
    if (activeFilter === 'hot') return leads.filter(l => l.lead_score >= hotThreshold);
    if (activeFilter === 'warm') return leads.filter(l => l.lead_score >= 40 && l.lead_score < hotThreshold);
    if (activeFilter === 'cool') return leads.filter(l => l.lead_score < 40 && l.lead_score !== null && l.lead_score !== undefined && l.lead_score !== '');
    if (activeFilter === 'email') return leads.filter(l => l.email || l.emails);
    return leads;
  };

  const filteredLeads = getFilteredLeads();
  const hotCount = leads.filter(l => l.lead_score >= hotThreshold).length;
  const warmCount = leads.filter(l => l.lead_score >= 40 && l.lead_score < hotThreshold).length;
  const coolCount = leads.filter(l => l.lead_score < 40 && l.lead_score !== null && l.lead_score !== undefined && l.lead_score !== '').length;
  const emailCount = leads.filter(l => l.email || l.emails).length;

  const hasActiveJobs = effectiveActiveJobs.length > 0;
  const hasQueuedJobs = effectiveQueuedJobs.length > 0;
  const hasLeads = leads.length > 0;
  const showEmptyState = !hasActiveJobs && !hasLeads && !loadingLastResults && !effectiveCompletedJob;

  const activeJob = effectiveActiveJobs[0];

  return (
    <div className="page-content" style={{ maxWidth: 'clamp(320px, 100%, 1000px)', margin: '0 auto' }}>
      
      {/* ================================================================ */}
      {/* HEADER + SEARCH CREDITS — side by side on desktop, stacked on mobile */}
      {/* ================================================================ */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 'var(--space-lg)', marginBottom: 'var(--space-xl)', flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={24} color="var(--accent-primary)" /> New Search
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: 0 }}>
            Discover high-intent leads with AI scoring and ready-to-send pitch angles.
          </p>
        </div>
        {!credits.loading && credits.limit > 0 && (
          <div style={{ flex: '0 1 250px', minWidth: 200 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500 }}>
                Search Credits
              </span>
              <span style={{ 
                color: credits.used / credits.limit > 0.9 ? 'var(--error)' : credits.used / credits.limit > 0.6 ? 'var(--warning)' : 'var(--text-secondary)',
                fontSize: 13, fontWeight: 600 
              }}>
                {credits.used} / {credits.limit}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: 'var(--border-primary)', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min((credits.used / credits.limit) * 100, 100)}%`,
                height: '100%',
                borderRadius: 3,
                background: credits.used / credits.limit > 0.9 
                  ? 'linear-gradient(90deg, #EF4444, #DC2626)' 
                  : credits.used / credits.limit > 0.6 
                    ? 'linear-gradient(90deg, #F59E0B, #D97706)' 
                    : 'linear-gradient(90deg, #10B981, #059669)',
                transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
              }} />
            </div>
            {credits.used / credits.limit > 0.75 && (
              <p style={{ color: 'var(--warning)', fontSize: 12, margin: '6px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>⚠️</span> 
                <a href="/dashboard/billing?tab=plans" style={{ color: 'var(--accent-primary)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                  Upgrade for more →
                </a>
              </p>
            )}
          </div>
        )}
      </div>

      {!isOnline && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderRadius: 'var(--radius-lg)', marginBottom: 'var(--space-xl)', background: 'var(--warning-bg)', border: '1px solid var(--warning)', color: 'var(--warning)', fontSize: 14 }}>
          <WifiOff size={20} />
          <div><div style={{ fontWeight: 600 }}>You're Offline</div><div style={{ fontSize: 12, opacity: 0.8 }}>Search requires an internet connection.</div></div>
          <button onClick={() => window.location.reload()} style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: '1px solid var(--warning)', background: 'transparent', color: 'var(--warning)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}><RefreshCw size={14} /> Retry</button>
        </div>
      )}


      {/* Completion Banner,  appears at top of page */}
      {completionBanner && (
        <div style={{
          background: 'linear-gradient(135deg, var(--success-bg), rgba(16,185,129,0.08))',
          border: '1px solid var(--success)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px 20px',
          marginBottom: 'var(--space-xl)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, animation: 'fadeInUp 0.4s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 28 }}>🎉</span>
            <div>
              <div style={{ color: 'var(--success)', fontWeight: 700, fontSize: 15 }}>
                Search Complete,  {completionBanner.count} Lead{completionBanner.count !== 1 ? 's' : ''} Found
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
                &ldquo;{completionBanner.query}&rdquo;
              </div>
            </div>
          </div>
          <button onClick={() => setCompletionBanner(null)} style={{
            background: 'none', border: 'none', color: 'var(--text-tertiary)',
            cursor: 'pointer', fontSize: 18, padding: 4,
          }}>✕</button>
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-xl)', background: 'var(--error-bg)', border: '1px solid var(--error)', color: 'var(--error)', fontSize: 13 }}>
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
        </div>
      )}

      {/* ================================================================ */}
      {/* ACTIVE SEARCH CARD */}
      {/* ================================================================ */}
      {hasActiveJobs && effectiveActiveJobs.length > 0 && (
        <>
          <ActiveSearchCard jobs={effectiveActiveJobs} activeCount={effectiveActiveJobs.length} />
          
          {/* Show additional running/queued jobs indicator */}
          {(effectiveActiveJobs.length > 1 || effectiveQueuedJobs.length > 0) && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
              marginBottom: 'var(--space-xl)', flexWrap: 'wrap',
            }}>
              {effectiveActiveJobs.length > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 'var(--radius-full)',
                  background: 'var(--accent-amber-glow)', border: '1px solid var(--accent-amber)',
                  color: 'var(--accent-amber)', fontSize: 'var(--text-xs)', fontWeight: 600,
                }}>
                  <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} />
                  {effectiveActiveJobs.length - 1} more search{effectiveActiveJobs.length > 2 ? 'es' : ''} running
                </div>
              )}
              {effectiveQueuedJobs.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 'var(--radius-full)',
                  background: 'var(--warning-bg)', border: '1px solid var(--warning)',
                  color: 'var(--warning)', fontSize: 'var(--text-xs)', fontWeight: 600,
                }}>
                  <Clock size={14} />
                  {effectiveQueuedJobs.length} queued
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Queued jobs */}
      {hasQueuedJobs && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)', marginBottom: 'var(--space-xl)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Clock size={16} color="var(--warning)" />
            <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 600, margin: 0 }}>Queued ({effectiveQueuedJobs.length} waiting)</h3>
          </div>
          {effectiveQueuedJobs.map(job => (
            <div key={job.job_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-primary)' }}>
              <div>
                <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{job.job_id?.startsWith('rescore_') ? 'Rescore ongoing' : `&ldquo;${job.display_name || job.query}&rdquo;`}</span>
                {job.queue_reason === 'concurrency_limit' && (
                  <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>
                    📊 Plan limit,  <a href="/dashboard/billing?tab=plans" style={{ color: 'var(--accent-primary)' }}>upgrade for more concurrent searches</a>
                  </span>
                )}
                {job.queue_reason === 'system_busy' && (
                  <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: 11, marginTop: 2 }}>
                    🖥️ All workers busy,  estimated wait ~{(job.position || 1) * 1.5} min
                  </span>
                )}
              </div>
              <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: 'var(--text-xs)', fontWeight: 600 }}>#{job.position || ', '}</span>
            </div>
          ))}
        </div>
      )}



      
      {/* Spacing between Search Credits and Search Command Center */}
      <div style={{ marginTop: 48, marginBottom: 48 }} />

      {/* ================================================================ */}
      {/* SEARCH COMMAND CENTER */}
      {/* ================================================================ */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: 'var(--space-xl) var(--space-2xl)', boxShadow: 'var(--shadow-md)', marginBottom: 'var(--space-xl)' }}>
        <form onSubmit={handleSearch}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '2 1 200px', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <label style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.3 }}>
                  Target Business Type
                </label>
                <div className="query-hints-container" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onMouseEnter={() => setShowQueryHints(true)}
                    onMouseLeave={() => setShowQueryHints(false)}
                    onClick={() => setShowQueryHints(!showQueryHints)}
                    style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: '1.5px solid var(--text-tertiary)', background: 'transparent',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 0, color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 700,
                      transition: 'all 0.15s ease', lineHeight: 1,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.color = 'var(--accent-primary)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--text-tertiary)'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                  >
                    ?
                  </button>
                  {showQueryHints && (
                    <div style={{
                      position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000,
                      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-lg)', padding: '16px 20px',
                      boxShadow: 'var(--shadow-xl)', width: 'calc(100vw - 48px)', maxWidth: 360,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                          💡 Search Tips
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowQueryHints(false)}
                          style={{
                            background: 'none', border: 'none', color: 'var(--text-tertiary)',
                            cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      </div>

                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                        <div style={{ marginBottom: 4 }}>✅ <strong>Industry + Location</strong> works best:</div>
                        <div style={{ paddingLeft: 12, marginBottom: 8, color: 'var(--text-tertiary)' }}>
                          • dentists in Chicago<br />
                          • hotels in Miami Beach<br />
                          • web design agencies San Francisco
                        </div>
                        <div style={{ marginBottom: 4 }}>✅ <strong>Be specific</strong> with your search:</div>
                        <div style={{ paddingLeft: 12, color: 'var(--text-tertiary)' }}>
                          • commercial cleaning services NYC<br />
                          • family law attorneys Houston<br />
                          • digital marketing agencies Lagos
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none', zIndex: 1 }} />
                <input ref={queryInputRef} type="text" value={businessType} onChange={(e) => setBusinessType(e.target.value)}
                  maxLength={40} placeholder="e.g., hotels, dentists, plumbers" disabled={searchLoading} autoComplete="off"
                  style={{ width: '100%', padding: '14px 16px 14px 44px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontSize: 'var(--text-base)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s ease, box-shadow 0.2s ease' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--accent-primary)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>            </div>
            <div style={{ flex: '2 1 200px', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <label style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.3 }}>
                  Location
                </label>
              </div>
              <div style={{ position: 'relative' }}>
                <MapPin size={18} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none', zIndex: 1 }} />
                <input type="text" value={location} onChange={handleLocationChange}
                  onFocus={() => setShowLocationSuggestions(locationSuggestions.length > 0)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  maxLength={60} placeholder="e.g., Chicago, Lagos, London" disabled={searchLoading} autoComplete="off"
                  style={{ width: '100%', padding: '14px 16px 14px 44px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontSize: 'var(--text-base)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s ease, box-shadow 0.2s ease' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--accent-primary)'; e.target.style.boxShadow = '0 0 0 3px var(--accent-glow)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
                  zIndex: 1000, maxHeight: 200, overflowY: 'auto', marginTop: 4,
                }}>
                  {locationLoading && (
                    <div style={{ padding: '8px 12px', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                      Searching locations...
                    </div>
                  )}
                  {locationSuggestions.map((sug, i) => (
                    <div
                      key={i}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        // Use the most specific location — city, state, or country
                        const locationName = sug.city || sug.state || sug.country || sug.name;
                        setLocation(locationName);
                        setLocationSuggestions([]);
                        setShowLocationSuggestions(false);
                      }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer',
                        borderBottom: i < locationSuggestions.length - 1 ? '1px solid var(--border-primary)' : 'none',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 500 }}>
                        {sug.city || sug.state || sug.country || sug.name}
                      </div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                        {sug.city && sug.state && `${sug.state}, `}
                        {sug.country}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" disabled={searchLoading || !businessType.trim() || !location.trim()} className="auth-button auth-button-primary"
              style={{ flex: '0 0 auto', height: 50, padding: '0 32px', fontSize: 'var(--text-base)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', opacity: (businessType.trim() && location.trim()) ? 1 : 0.5 }}>
              {searchLoading ? <><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /> Searching...</> : <><Search size={18} /> Search</>}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-md)', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500, whiteSpace: 'nowrap' }}><Briefcase size={14} /> Service:</span>
            {showServiceEdit ? (
              <input type="text" value={serviceContext} onChange={(e) => setServiceContext(e.target.value)}
                onBlur={() => { setShowServiceEdit(false); handleServiceContextBlur(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { setShowServiceEdit(false); handleServiceContextBlur(); } }}
                placeholder="What do you sell?" autoFocus
                style={{ padding: '6px 12px', background: 'var(--input-bg)', border: '1px solid var(--accent-primary)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)', outline: 'none', minWidth: 200 }} />
            ) : (
              <button onClick={() => setShowServiceEdit(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-full)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                {serviceContext || 'B2B software'} <span style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>✎</span>
              </button>
            )}
          </div>
          {user?.organization_id && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginTop: 'var(--space-md)', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500, whiteSpace: 'nowrap' }}>🏷️ Campaign:</span>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., Q3-Outbound-Lagos"
                maxLength={60}
                style={{ padding: '6px 12px', background: 'var(--input-bg)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', fontSize: 'var(--text-sm)', outline: 'none', minWidth: 220 }}
              />
            </div>
          )}
        </form>

      
      </div>

      {/* Horizontal Divider — between search card and results */}
      <div style={{
        borderTop: '1px solid var(--border-primary)',
        marginTop: 48,
        marginBottom: 48,
      }} />

      {/* ================================================================ */}
      {/* RESULTS SECTION */}
      {/* ================================================================ */}
      <div ref={resultsRef}>
        {hasLeads && (
          <div className={showSuccessPulse ? 'success-pulse' : ''}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 'var(--space-lg)' }}>
              <div>
                <h2 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-xl)', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sparkles size={20} color="var(--accent-primary)" /> {leads.length} Lead{leads.length !== 1 ? 's' : ''} Found - Latest
                </h2>
                {completedQuery && <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>Search: <strong>"{completedQuery}"</strong></p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleExportCSV} disabled={exporting} style={{ padding: '4px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--success)', background: 'transparent', color: 'var(--success)', cursor: 'pointer', fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}><Download size={12} /> CSV</button>
                <button onClick={() => navigate('/dashboard/history')} style={{ padding: '4px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>View All <ChevronRight size={12} /></button>
              </div>
            </div>

            {/* Contextual Upsell Banner */}
            {!upsellDismissed && plan === 'free' && leads.length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, var(--accent-glow), rgba(245,158,11,0.06))',
                border: '1px solid var(--border-accent)',
                borderRadius: 'var(--radius-lg)',
                padding: '14px 18px',
                marginBottom: 'var(--space-lg)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                  <span style={{ fontSize: 18 }}>🚀</span>
                  <span>
                    {leads.length >= 5 
                      ? <>Found {leads.length} leads? Imagine <strong style={{ color: 'var(--accent-primary)' }}>15 leads per search</strong> with Growth,  that's 3x more opportunities.</>
                      : <>Upgrade to <strong style={{ color: 'var(--accent-primary)' }}>Growth</strong> for 15 leads per search, AI scoring, and CRM exports.</>
                    }
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => navigate('/dashboard/billing?tab=plans')} style={{
                    background: 'var(--accent-gradient)', color: '#fff', border: 'none',
                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                  }}>See Plans</button>
                  <button onClick={() => setUpsellDismissed(true)} style={{
                    background: 'none', border: 'none', color: 'var(--text-tertiary)',
                    cursor: 'pointer', fontSize: 16, padding: '0 4px',
                  }}>✕</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-xl)' }}>
              {[
                { key: 'all', label: 'All', count: leads.length, color: 'var(--text-secondary)' },
                { key: 'hot', label: '🔥 Hot', count: hotCount, color: 'var(--success)' },
                { key: 'warm', label: '🟡 Warm', count: warmCount, color: 'var(--warning)' },
                { key: 'cool', label: '🔵 Cool', count: coolCount, color: 'var(--info)' },
                { key: 'email', label: '📧 Has Email', count: emailCount, color: 'var(--accent-secondary)' },
              ].map((filter) => (
                <button key={filter.key} onClick={() => setActiveFilter(filter.key)} style={{
                  padding: '4px 10px', borderRadius: 'var(--radius-full)',
                  border: activeFilter === filter.key ? `2px solid ${filter.color}` : '1px solid var(--border-primary)',
                  background: activeFilter === filter.key ? `${filter.color}15` : 'transparent',
                  color: activeFilter === filter.key ? filter.color : 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: activeFilter === filter.key ? 600 : 500, transition: 'all 0.15s ease',
                }}>{filter.label} ({filter.count})</button>
              ))}
            </div>

            {/* Filter-based upsell: few email results */}
            {!upsellDismissed && plan === 'free' && activeFilter === 'email' && emailCount <= 2 && leads.length > 0 && (
              <div style={{
                background: 'var(--warning-bg)', border: '1px solid var(--warning)',
                borderRadius: 'var(--radius-md)', padding: '12px 16px',
                marginBottom: 'var(--space-md)', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12, fontSize: 12,
              }}>
                <span style={{ color: 'var(--warning)' }}>
                  💡 Only {emailCount} lead{emailCount !== 1 ? 's' : ''} with email found. Upgrade to Growth for phone numbers and social profiles.
                </span>
                <button onClick={() => navigate('/dashboard/billing?tab=plans')} style={{
                  background: 'none', border: 'none', color: 'var(--accent-primary)',
                  cursor: 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
                }}>Upgrade →</button>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 360px), 1fr))', gap: 'var(--space-lg)' }}>
              {filteredLeads.map((lead, index) => (
                <LeadCard key={`${lead.company_name}-${index}`} lead={lead} index={index} isSelected={false} onToggleSelect={() => {}} copiedPitch={copiedPitch} isProcessing={false} animated={true}
                  plan={plan}
                  hotThreshold={hotThreshold}
                  onCopyPitch={() => copyPitch(lead, index)}
                  onClick={() => navigate(`/dashboard/company/${encodeURIComponent(lead.company_name)}`)}
                  onUpgradeClick={() => navigate('/dashboard/billing?tab=plans')}
                  onWebsiteClick={(e) => { e.stopPropagation(); if (lead.website) window.open(lead.website, '_blank'); }} />
              ))}
            </div>

            {filteredLeads.length === 0 && (
              <div style={{ textAlign: 'center', padding: 'var(--space-3xl)', color: 'var(--text-secondary)' }}>
                <p style={{ fontSize: 'var(--text-base)' }}>No leads match this filter.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* EMPTY STATE */}
      {/* ================================================================ */}
      {showEmptyState && !loadingLastResults && (
        <div style={{ textAlign: 'center', padding: 'clamp(40px, 8vw, 60px) 24px', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto var(--space-xl)' }}>
            <Target size={36} color="var(--accent-primary)" />
          </div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-xl)', fontWeight: 600, margin: '0 0 8px' }}>Ready to Find Leads?</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', maxWidth: 440, margin: '0 auto var(--space-xl)', lineHeight: 1.6 }}>
            Enter a search query above to discover businesses on Google Maps. Each result comes with AI scoring, contact details, and a personalized pitch angle.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-md)', maxWidth: 500, margin: '0 auto' }}>
            {[
              { step: 1, text: 'Enter a search query' }, { step: 2, text: 'We scan Google Maps' },
              { step: 3, text: 'Extract contact details' }, { step: 4, text: 'AI scores every lead' },
            ].map(item => (
              <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 'var(--space-md)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontSize: 'var(--text-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.step}</div>
                <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)' }}>{item.text}</span>
              </div>
            ))}
          </div>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-xl)' }}>Try: "dentists in Chicago" or "web design agencies San Francisco"</p>
        </div>
      )}

      {loadingLastResults && !hasActiveJobs && (
        <div style={{ textAlign: 'center', padding: 'var(--space-3xl)', background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--accent-primary)', marginBottom: 16 }} />
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading your last results...</p>
        </div>
      )}

      {showUpgradeModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }} onClick={() => setShowUpgradeModal(false)}>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 20, padding: '28px 32px', maxWidth: 480, width: '90%', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-xl)', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 8px' }}>Service Context Change Limit Reached</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 20px', lineHeight: 1.6 }}>You've used your changes for this month. Upgrade to unlock more.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowUpgradeModal(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Maybe Later</button>
              <button onClick={() => { setShowUpgradeModal(false); navigate('/dashboard/billing?tab=plans'); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: 'var(--accent-gradient)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>🚀 View Plans</button>
            </div>
          </div>
        </div>, document.body
      )}
    </div>
  );
}

// ================================================================
// ACTIVE SEARCH CARD (extracted for clarity)
// ================================================================
function ActiveSearchCard({ jobs, activeCount }) {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  
  // Reset index when jobs array changes
  React.useEffect(() => {
    if (currentIndex >= jobs.length) {
      setCurrentIndex(Math.max(0, jobs.length - 1));
    }
  }, [jobs.length, currentIndex]);
  
  const job = jobs[currentIndex];
  const totalJobs = jobs.length;
  
  if (!job) return null;

  const progressPercent = job.progress_percent ?? 5;
  
  const goNext = () => setCurrentIndex((prev) => (prev + 1) % totalJobs);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + totalJobs) % totalJobs);
  
  const checklist = getChecklist(progressPercent);

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '2px solid var(--accent-amber-glow)',
      borderRadius: 'var(--radius-xl)',
      padding: 'var(--space-2xl)',
      boxShadow: 'var(--shadow-glow)',
      marginBottom: 'var(--space-xl)',
      animation: 'scaleIn 0.3s ease',
    }}>
      {/* Card stack navigation */}
      {totalJobs > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, marginBottom: 'var(--space-lg)',
        }}>
          <button onClick={goPrev} style={navBtnStyle}>
            <ChevronLeft size={14} />
          </button>
          <span style={{
            color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500,
            minWidth: 60, textAlign: 'center',
          }}>
            {currentIndex + 1} of {totalJobs}
          </span>
          <button onClick={goNext} style={navBtnStyle}>
            <ChevronRight size={14} />
          </button>
          <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
            {jobs.map((_, i) => (
              <button key={i} onClick={() => setCurrentIndex(i)} style={{
                width: i === currentIndex ? 16 : 6, height: 6, borderRadius: 3,
                border: 'none', background: i === currentIndex ? 'var(--accent-amber)' : 'var(--border-primary)',
                cursor: 'pointer', transition: 'all 0.2s ease',
              }} />
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--accent-amber-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--accent-amber)' }} />
          </div>
          <div>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 600, margin: 0 }}>{job.job_id?.startsWith('rescore_') ? 'Rescore ongoing' : `Searching: "${job.display_name || job.query}"`}</h3>
            {totalJobs > 1 && <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', margin: '2px 0 0' }}>+{totalJobs - 1} more search{totalJobs > 2 ? 'es' : ''} running</p>}
          </div>
        </div>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={12} /> {job.started_at ? formatElapsed(job.started_at) : 'Just started'}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>{Math.round(progressPercent)}%</span>
        </div>
        <div style={{ background: 'var(--border-primary)', borderRadius: 'var(--radius-full)', height: 8, overflow: 'hidden' }}>
          <div style={{
            width: `${Math.max(progressPercent, 5)}%`, height: '100%',
            background: 'linear-gradient(90deg, var(--accent-amber), var(--accent-primary))',
            borderRadius: 'var(--radius-full)', transition: 'width 0.5s ease',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, left: '-100%', width: '100%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)', animation: 'progressShine 1.5s ease-in-out infinite' }} />
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {checklist.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            color: item.done ? 'var(--success)' : item.active ? 'var(--accent-amber)' : 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)', fontWeight: item.active ? 500 : 400,
            transition: 'color 0.3s ease',
          }}>
            {item.done ? (
              <Check size={16} style={{ flexShrink: 0, animation: 'notificationBounce 0.4s ease' }} />
            ) : item.active ? (
              <Loader2 size={16} style={{ flexShrink: 0, animation: 'spin 0.8s linear infinite' }} />
            ) : (
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid var(--border-primary)', flexShrink: 0 }} />
            )}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-lg)', marginBottom: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
        <Clock size={12} /> {estimateTime(progressPercent)}
      </p>
    </div>
  );
}

// ================================================================
// LEAD CARD
// ================================================================
function LeadCard({ lead, index, copiedPitch, onCopyPitch, onClick, onWebsiteClick, onUpgradeClick, plan, hotThreshold = 70 }) {
  const score = lead.lead_score;
  const scoreLevel = score >= hotThreshold ? 'hot' : score >= 40 ? 'warm' : 'cool';
  const scoreColors = {
    hot: { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success)' },
    warm: { bg: 'var(--warning-bg)', color: 'var(--warning)', border: 'var(--warning)' },
    cool: { bg: 'var(--info-bg)', color: 'var(--info)', border: 'var(--info)' },
  };
  const colors = scoreColors[scoreLevel] || scoreColors.cool;
  const socials = getSocialsFromLead(lead);

  return (
    <div onClick={onClick} style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', cursor: 'pointer',
      transition: 'all 0.2s ease', animation: `fadeSlideIn 0.4s ease ${index * 0.05}s both`,
      display: 'flex', flexDirection: 'column', gap: 'var(--space-md)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = colors.border; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.borderColor = 'var(--border-primary)'; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 600, margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.company_name}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {lead.industry && <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: 'var(--radius-full)' }}>{lead.industry}</span>}
            {(lead.city || lead.country) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={14} color="var(--text-tertiary)" /><span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>{[lead.city, lead.state, lead.country].filter(Boolean).join(', ')}</span></div>
            )}
            {lead.google_rating && <span style={{ color: '#f59e0b', fontSize: 'var(--text-xs)', fontWeight: 600, whiteSpace: 'nowrap' }}>⭐ {lead.google_rating}<span style={{ color: 'var(--text-tertiary)', fontWeight: 400, marginLeft: 4 }}>({lead.google_review_count || 0})</span></span>}
      </div>
        </div>
        {score && <div style={{ padding: '4px 12px', borderRadius: 'var(--radius-full)', background: colors.bg, color: colors.color, fontSize: 'var(--text-sm)', fontWeight: 700, flexShrink: 0 }}>{score}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        {(lead.email || lead.emails) && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--success)', fontSize: 'var(--text-xs)' }}><Mail size={14} /> </span>}
        {(lead.phone || lead.phones) && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--success)', fontSize: 'var(--text-xs)' }}><Phone size={14} /> </span>}
        {lead.website && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--info)', fontSize: 'var(--text-xs)' }}><ExternalLink size={14} /> Website</span>}
        {socials.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
                      {socials.map((s, i) => (
                        <span key={i} title={s.platform} style={{ cursor: 'pointer', opacity: 1 }}>
                          <SocialLink platform={s.platform} url={s.url} size={14} />
                        </span>
                      ))}
                    </div>
                  )}
      </div>
      {lead.pitch_angle && (
        <div style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', padding: 'var(--space-md)', border: '1px solid var(--border-primary)' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontStyle: 'italic' }}>{(() => {
  try {
    const p = lead.pitch_angle;
    if (typeof p === 'string' && p.startsWith('[')) {
      const angles = JSON.parse(p);
      const first = angles[0] || '';
      return '"' + first.replace(/^\[?(Empathy|ROI|Urgency|Data-Driven|Direct|Data)(\s*angle)?[\s:]*[, –-]?\s*/i, '').replace(/\]$/, '').trim() + '"';
    }
    return '"' + (p || '') + '"';
  } catch { const cleaned = (lead.pitch_angle || '').replace(/^[",\s]+/, '').replace(/[",\s]+$/, ''); return '"' + (cleaned || 'No pitch angle yet') + '"'; }
})()}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {(lead.pitch_angle || true) && (
        <button onClick={(e) => { e.stopPropagation(); onCopyPitch(); }} style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 'var(--radius-md)',
          border: `2px solid ${copiedPitch === index ? 'var(--success)' : 'var(--accent-amber)'}`,
          background: copiedPitch === index ? 'var(--success-bg)' : 'transparent',
          color: copiedPitch === index ? 'var(--success)' : 'var(--accent-amber)',
          cursor: 'pointer', fontSize: 'var(--text-xs)', fontWeight: 600, transition: 'all 0.2s ease',
        }}>{copiedPitch === index ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Pitch</>}</button>
        )}
        {lead.website && (
          <button onClick={onWebsiteClick} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.15s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--info)'; e.currentTarget.style.color = 'var(--info)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}><ExternalLink size={16} /></button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onClick(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'all 0.15s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.color = 'var(--accent-primary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}><ChevronRight size={18} /></button>
      </div>
    </div>
  );
}

// ================================================================
// HELPERS
// ================================================================
function formatElapsed(startedAt) {
  if (!startedAt) return 'Just started';
  let date = new Date(startedAt);
  // Normalize server timestamps without timezone marker to UTC
  if (isNaN(date.getTime()) || (!startedAt.endsWith('Z') && !startedAt.includes('+') && !startedAt.includes('-'))) {
    date = new Date(startedAt + 'Z');
  }
  const elapsed = Math.floor((Date.now() - date.getTime()) / 1000);
  if (elapsed < 60) return `${elapsed}s ago`;
  if (elapsed < 3600) return `${Math.floor(elapsed / 60)}m ago`;
  return `${Math.floor(elapsed / 3600)}h ago`;
}

const navBtnStyle = {
  width: 28, height: 28, borderRadius: '50%',
  border: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)',
  color: 'var(--text-secondary)', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s ease',
};

function estimateTime(percent) {
  if (percent >= 96) return 'Almost done...';
  if (percent >= 76) return '~30-60 seconds remaining';
  if (percent >= 36) return '~1-2 minutes remaining';
  if (percent >= 6) return '~2-3 minutes remaining';
  return 'Starting up...';
}

// ─── FIXED CHECKLIST,  items are cumulative, once done they STAY done ───
function getChecklist(percent, progressText) {
  const pct = Math.round(percent);
  
  const items = [
    { label: 'Initializing search...',              done: pct >= 6,   active: pct >= 0 && pct < 6 },
    { label: 'Discovering businesses',   done: pct >= 36,  active: pct >= 6 && pct < 36 },
    { label: 'Enriching with contact details',      done: pct >= 76,  active: pct >= 36 && pct < 76 },
    { label: 'AI scoring leads',                    done: pct >= 96,  active: pct >= 76 && pct < 96 },
    { label: 'Finalizing results',                  done: pct >= 99,  active: pct >= 96 && pct < 100 },
  ];
  
  return items;
}
